// 一个活动（子 key）对应 N 行 common_config：configValue 内容共享，每行一条灰度规则（matchRule）。
// 内容序列化与「规则 ↔ matchRule」解析相互独立。

export interface GrayscaleRule {
    // 对应已存在的 common_config 行；新增规则时为空（id 为 GraphQL ID，字符串）
    id?: string;
    sortOrder?: number;
    startDate?: string;
    endDate?: string;
    startHour?: number;
    endHour?: number;
    // 表单用字符串标签，序列化时转 number
    userIds?: string[];
    warehouseIds?: string[];
}

export interface ActivityContent {
    nameZh: string;
    nameEn: string;
    nameEs: string;
    link: string;
    icon: string;
    badge?: string | null;
}

export interface ActivityFormValues {
    nameZh: string;
    nameEn: string;
    nameEs: string;
    link: string;
    icon: string;
    badge?: string;
    rules?: GrayscaleRule[];
}

export interface SerializedRule {
    id?: string;
    sortOrder?: number;
    matchRule: string;
}

function parseIdList(raw?: string[]): number[] {
    return (raw ?? [])
        .map((s) => String(s).trim())
        .filter(Boolean)
        .map(Number)
        .filter((n) => !isNaN(n));
}

export function buildRuleMatchRule(rule: GrayscaleRule): string {
    const parts: string[] = [];
    if (rule.startDate) {
        parts.push(`#currentDate >= '${rule.startDate}'`);
    }
    if (rule.endDate) {
        parts.push(`#currentDate <= '${rule.endDate}'`);
    }
    if (rule.startHour !== undefined && rule.startHour !== null) {
        parts.push(`#currentHour >= ${rule.startHour}`);
    }
    if (rule.endHour !== undefined && rule.endHour !== null) {
        parts.push(`#currentHour < ${rule.endHour}`);
    }
    const userIds = parseIdList(rule.userIds);
    if (userIds.length > 0) {
        const list = userIds.map((id) => `${id}L`).join(',');
        parts.push(`#userId != null && {${list}}.contains(#userId)`);
    }
    const warehouseIds = parseIdList(rule.warehouseIds);
    if (warehouseIds.length > 0) {
        const list = warehouseIds.map((id) => `${id}L`).join(',');
        parts.push(`#warehouseId != null && {${list}}.contains(#warehouseId)`);
    }
    return parts.join(' && ');
}

export function parseRuleMatchRule(matchRule?: string): GrayscaleRule {
    if (!matchRule) return {};
    const parseIds = (variable: string): string[] | undefined => {
        const m = matchRule.match(new RegExp(`\\{([^}]*)\\}\\.contains\\(#${variable}\\)`));
        if (!m) return undefined;
        const ids = m[1]
            .split(',')
            .map((s) => s.trim().replace(/L$/i, ''))
            .filter(Boolean);
        return ids.length > 0 ? ids : undefined;
    };
    const startDate = matchRule.match(/#currentDate >= '(\d{4}-\d{2}-\d{2})'/)?.[1];
    const endDate = matchRule.match(/#currentDate <= '(\d{4}-\d{2}-\d{2})'/)?.[1];
    const startHourStr = matchRule.match(/#currentHour >= (\d+)/)?.[1];
    const endHourStr = matchRule.match(/#currentHour < (\d+)/)?.[1];
    return {
        startDate,
        endDate,
        startHour: startHourStr !== undefined ? Number(startHourStr) : undefined,
        endHour: endHourStr !== undefined ? Number(endHourStr) : undefined,
        userIds: parseIds('userId'),
        warehouseIds: parseIds('warehouseId'),
    };
}

export function parseActivityContent(configValue: string): ActivityFormValues {
    try {
        const parsed: ActivityContent = JSON.parse(configValue);
        return {
            nameZh: parsed.nameZh ?? '',
            nameEn: parsed.nameEn ?? '',
            nameEs: parsed.nameEs ?? '',
            link: parsed.link ?? '',
            icon: parsed.icon ?? '',
            badge: parsed.badge ?? undefined,
        };
    } catch {
        return { nameZh: '', nameEn: '', nameEs: '', link: '', icon: '' };
    }
}

export function serializeActivityContent(values: ActivityFormValues): string {
    const content: ActivityContent = {
        nameZh: values.nameZh,
        nameEn: values.nameEn,
        nameEs: values.nameEs,
        link: values.link,
        icon: values.icon,
        badge: values.badge || null,
    };
    return JSON.stringify(content);
}

// 至少保留一条规则：无规则意味着活动没有任何 common_config 行、永不展示。
// 空规则（无任何条件）的 matchRule 为空串，后端视为无条件命中（全量展示）。
export function serializeRules(rules?: GrayscaleRule[]): SerializedRule[] {
    const list = rules && rules.length > 0 ? rules : [{}];
    return list.map((rule) => ({
        id: rule.id,
        sortOrder: rule.sortOrder,
        matchRule: buildRuleMatchRule(rule),
    }));
}

export function generateActivitySubKey(): string {
    return `app.activity.${Date.now()}`;
}
